import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ExternalLink, Search, Sparkles } from 'lucide-react';
import { ticketsApi } from '../../api/tickets';
import Modal from '../Modal';
import { StatusBadge } from '../badges';
import type { TicketStatus } from '../../types';

interface CandidateTicket {
  id: string;
  ticketNumber: string;
  title: string;
  status: string;
  /** Similarity 0..1 — only on "similar" suggestions, not number lookups. */
  score?: number;
}

/* Link-tickets dialog: type ticket numbers to pull in specific tickets,
   and/or pick from the keyword-ranked "similar" suggestions — check any
   number of them and link in one action. Mount conditionally (`open &&`)
   so state resets between uses. */
export default function LinkTicketsModal({
  open,
  onClose,
  ticketId,
  linkedIds,
}: {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  /** Already-linked ticket ids (relations, parent, subs) — filtered out. */
  linkedIds: string[];
}) {
  const queryClient = useQueryClient();
  const [numbersInput, setNumbersInput] = useState('');
  const [found, setFound] = useState<CandidateTicket[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const excluded = new Set([ticketId, ...linkedIds]);

  const { data: similar = [], isLoading: similarLoading } = useQuery({
    queryKey: ['ticket-similar', ticketId],
    queryFn: () => ticketsApi.getSimilar(ticketId),
    enabled: open,
  });

  const lookupMutation = useMutation({
    mutationFn: (numbers: string) => ticketsApi.lookupByNumbers(numbers),
    onSuccess: (tickets) => {
      const fresh = tickets.filter(t => !excluded.has(t.id));
      if (fresh.length === 0) {
        toast.error('No matching tickets found (already-linked ones are hidden)');
        return;
      }
      setFound(prev => {
        const known = new Set(prev.map(p => p.id));
        return [...prev, ...fresh.filter(t => !known.has(t.id))];
      });
      // Looking a number up is intent to link it — pre-select the results.
      setSelected(prev => [...new Set([...prev, ...fresh.map(t => t.id)])]);
      setNumbersInput('');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const linkMutation = useMutation({
    mutationFn: (ids: string[]) => ticketsApi.linkTickets(ticketId, ids),
    onSuccess: ({ linked }) => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket-activity', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket-similar', ticketId] });
      toast.success(linked.length === 1
        ? `Linked ${linked[0].ticketNumber}`
        : `Linked ${linked.length} tickets`);
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const toggle = (id: string) =>
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const handleLookup = (e: React.FormEvent) => {
    e.preventDefault();
    if (numbersInput.trim()) lookupMutation.mutate(numbersInput.trim());
  };

  const suggestions = (similar as CandidateTicket[]).filter(
    t => !excluded.has(t.id) && !found.some(f => f.id === t.id),
  );

  const Row = ({ t }: { t: CandidateTicket }) => (
    <label className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-row-odd cursor-pointer">
      <input
        type="checkbox"
        className="w-3.5 h-3.5 accent-[var(--scpa-primary)] shrink-0"
        checked={selected.includes(t.id)}
        onChange={() => toggle(t.id)}
      />
      <span className="font-mono text-xs text-fg shrink-0">{t.ticketNumber}</span>
      <span className="text-sm text-fg truncate flex-1" title={t.title}>{t.title}</span>
      {typeof t.score === 'number' && (
        <span className="text-[11px] text-muted shrink-0">{Math.round(t.score * 100)}% match</span>
      )}
      <StatusBadge status={t.status as TicketStatus} />
      <a
        href={`/tickets/${t.id}`}
        target="_blank"
        rel="noreferrer"
        onClick={e => e.stopPropagation()}
        className="text-muted hover:text-primary shrink-0"
        title="View ticket in a new tab"
      >
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </label>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Link tickets"
      width={640}
      busy={linkMutation.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={linkMutation.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={selected.length === 0 || linkMutation.isPending}
            onClick={() => linkMutation.mutate(selected)}
          >
            {linkMutation.isPending
              ? 'Linking…'
              : `Link selected${selected.length > 0 ? ` (${selected.length})` : ''}`}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* By ticket number */}
        <form onSubmit={handleLookup} className="space-y-1.5">
          <label className="label" htmlFor="link-ticket-numbers">By ticket number</label>
          <div className="flex gap-2">
            <input
              id="link-ticket-numbers"
              className="input text-sm flex-1"
              placeholder="e.g. 42, 118 or cTKT-00042 — several at once is fine"
              value={numbersInput}
              onChange={e => setNumbersInput(e.target.value)}
            />
            <button
              type="submit"
              className="btn btn-secondary shrink-0"
              disabled={!numbersInput.trim() || lookupMutation.isPending}
            >
              <Search className="w-3.5 h-3.5" /> {lookupMutation.isPending ? 'Finding…' : 'Find'}
            </button>
          </div>
        </form>

        {found.length > 0 && (
          <div className="space-y-0.5">{found.map(t => <Row key={t.id} t={t} />)}</div>
        )}

        {/* Similar suggestions */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted mb-1.5 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Similar tickets
          </p>
          {similarLoading ? (
            <p className="text-xs text-muted py-2">Looking for similar tickets…</p>
          ) : suggestions.length === 0 ? (
            <p className="text-xs text-muted py-2">No similar tickets found.</p>
          ) : (
            <div className="space-y-0.5">{suggestions.map(t => <Row key={t.id} t={t} />)}</div>
          )}
        </div>
      </div>
    </Modal>
  );
}
